import { useEffect, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  PRODUCTS,
  findApplication,
  isProductKey,
  normalizeOrigin,
  type ApplicationRegistry,
  type ProductKey,
} from '@traceio-org/platform-contracts';

/**
 * The way back to the product a person came from (Trace Enterprise opens the hub's settings, invitations or billing
 * in a tab of its own, and the person needs a way back to it).
 *
 * A product links to the hub with ?from=<product key>. The hub reads it on any page, remembers it for the tab
 * (sessionStorage, so it survives moving between settings sections and reloads; an in-memory copy when storage is
 * blocked) and takes it out of the address, so an address copied from the bar stays clean. A later ?from= replaces it.
 *
 * Security: the parameter only names a product (one of PRODUCT_KEYS). The link always goes to that product's
 * destination in the application registry the hub loads from platform-api, never to an address taken from the query,
 * so this cannot become an open redirect. A product with no destination in this environment (or no registry at all)
 * shows no link.
 *
 * The parameter shares its name with two values that are not product keys and are left alone: the billing page's
 * from=payment (back from the payment provider) and the audit page's from=<day> filter.
 */
export const CAME_FROM_PARAM = 'from';
/** Read by the static first paint too (vite/settings-placeholder-shell.ts): keep the two in step. */
export const CAME_FROM_KEY = 'trace.hub.cameFrom';

/** Used when sessionStorage throws (blocked storage, some private modes): the tab still remembers until a reload. */
let memory: ProductKey | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** The product this tab came from, or null. Never throws. */
export function readCameFrom(): ProductKey | null {
  try {
    const stored = window.sessionStorage.getItem(CAME_FROM_KEY);
    if (stored !== null) return isProductKey(stored) ? stored : null;
  } catch {
    // Storage blocked: fall back to the in-memory copy.
  }
  return memory;
}

export function rememberCameFrom(productKey: ProductKey): void {
  memory = productKey;
  try {
    window.sessionStorage.setItem(CAME_FROM_KEY, productKey);
  } catch {
    // Storage blocked: the in-memory copy keeps the link for this page load.
  }
  notify();
}

/** Forgets the product (tests; nothing in the hub needs it otherwise). */
export function forgetCameFrom(): void {
  memory = null;
  try {
    window.sessionStorage.removeItem(CAME_FROM_KEY);
  } catch {
    // Nothing stored, or storage blocked.
  }
  notify();
}

/**
 * The product a query string names in ?from=, and the query without it. Only a single value that is a product key
 * counts; anything else (from=payment, an audit day, repeated or unknown values) leaves the query exactly as it was.
 */
export function takeCameFrom(search: string): { productKey: ProductKey | null; search: string } {
  const params = new URLSearchParams(search);
  const values = params.getAll(CAME_FROM_PARAM);
  const [only] = values;
  if (values.length !== 1 || !isProductKey(only)) return { productKey: null, search };
  params.delete(CAME_FROM_PARAM);
  const rest = params.toString();
  return { productKey: only, search: rest ? `?${rest}` : '' };
}

export interface ProductReturn {
  productKey: ProductKey;
  /** "Trace Enterprise": the registry's display name, or the product's own name when the registry has none. */
  name: string;
  /** The product's registered destination (its origin and default path). Never anything from the query. */
  href: string;
}

/** The way back to a product, from the registry only; null without a registry or a destination for it. */
export function productReturn(
  registry: ApplicationRegistry | null,
  productKey: ProductKey | null,
): ProductReturn | null {
  if (!registry || !productKey) return null;
  const application = findApplication(registry, productKey);
  const destination = application?.destination;
  if (!application || !destination || application.status === 'unavailable') return null;
  let href: string;
  try {
    const origin = normalizeOrigin(destination.origin);
    const url = new URL(destination.defaultPath, origin);
    // The registry already restricts defaultPath to a same-origin path; checked again so the link can only ever
    // leave for the registered origin.
    if (url.origin !== origin) return null;
    href = url.toString();
  } catch {
    return null;
  }
  const name = application.displayName.trim() || PRODUCTS[productKey].displayName;
  return { productKey, name, href };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The product this tab came from, kept current when a later ?from= replaces it. */
export function useCameFrom(): ProductKey | null {
  // The hub renders in the browser only; the static markup tests draw the same as the browser (readCameFrom never
  // throws, even without a window).
  return useSyncExternalStore(subscribe, readCameFrom, readCameFrom);
}

/**
 * Mounted once at the root: reads ?from=<product> on any page, remembers it and replaces the address without it
 * (same path, other parameters, hash and router state kept).
 */
export function useCaptureCameFrom(): void {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const { productKey, search } = takeCameFrom(location.search);
    if (!productKey) return;
    rememberCameFrom(productKey);
    void navigate(
      { pathname: location.pathname, search, hash: location.hash },
      { replace: true, state: location.state as unknown },
    );
  }, [location, navigate]);
}

/** How long the hub waits for the tab to close before it navigates instead. */
export const CLOSE_FALLBACK_MS = 300;

/**
 * The back link's click: the product opened the hub in a tab of its own (window.open), so closing that tab returns
 * the person to the product tab that opened it, which is still there with their work. Browsers only let a script close
 * a tab a script opened (or one with a single history entry); when the tab stays open (the hub was opened directly,
 * or by a plain link and then browsed) the link navigates to the product instead, as a plain link would. A modified
 * click (new tab or window) keeps the browser's default.
 */
export function returnToProduct(
  event: Pick<MouseEvent, 'preventDefault' | 'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>,
  href: string,
  win: Pick<Window, 'close' | 'closed' | 'setTimeout'> & { location: Pick<Location, 'assign'> } = window,
): void {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  win.close();
  win.setTimeout(() => {
    if (!win.closed) win.location.assign(href);
  }, CLOSE_FALLBACK_MS);
}
